// frontend/src/utils/authHelper.js
import axios from 'axios';

// Check if token is close to expiring (within 7 days)
const isTokenNearExpiry = (token) => {
  try {
    // Decode JWT payload (without verification, just for checking expiry)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const sevenDays = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    
    return (expiryTime - currentTime) < sevenDays;
  } catch (error) {
    console.error('Error checking token expiry:', error);
    return true; // If we can't parse, assume it needs refresh
  }
};

// Check if token is temporary (requires 2FA)
const isTemporaryToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.tempAuth === true;
  } catch (error) {
    return false;
  }
};

// Refresh the token if it's close to expiring
const refreshTokenIfNeeded = async (currentToken) => {
  try {
    // Don't try to refresh temporary tokens - they should expire and force 2FA completion
    if (isTemporaryToken(currentToken)) {
      console.log('Temporary token detected, skipping refresh');
      return currentToken;
    }
    
    if (!isTokenNearExpiry(currentToken)) {
      return currentToken; // Token is still fresh
    }
    
    console.log('Token is close to expiry, refreshing...');
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      
      if (response.data.success && response.data.token) {
        console.log('✅ Token refreshed successfully');
        localStorage.setItem('token', response.data.token);
        sessionStorage.setItem('token', response.data.token);
        
        // Dispatch custom event to notify WebSocket manager of token refresh
        window.dispatchEvent(new CustomEvent('tokenRefreshed', {
          detail: { token: response.data.token }
        }));
        
        return response.data.token;
      } else {
        console.warn('⚠️ Refresh response missing token, using current token');
        return currentToken;
      }
    } catch (refreshError) {
      console.error('❌ Failed to refresh token via API:', refreshError);
      return currentToken; // Return original token if refresh fails
    }
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return currentToken; // Return original token if refresh fails
  }
};

export const verifyAuth = async () => {
  const token = localStorage.getItem('token');
  const sessionToken = sessionStorage.getItem('token');
  
  if (!token && !sessionToken) {
    clearAuth();
    return false;
  }

  try {
    // Use the fresher token (localStorage preferred)
    const currentToken = token || sessionToken;
    
    // Try to refresh token if it's close to expiring
    const refreshedToken = await refreshTokenIfNeeded(currentToken);
    
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/verify`, {
      headers: { Authorization: `Bearer ${refreshedToken}` }
    });

    if (!response.data.success) {
      clearAuth();
      return false;
    }

    // Keep session alive with the refreshed token
    sessionStorage.setItem('token', refreshedToken);
    sessionStorage.setItem('user', localStorage.getItem('user') || sessionStorage.getItem('user'));

    return true;
  } catch (error) {
    console.error('Auth verification failed:', error);
    clearAuth();
    return false;
  }
};

export const clearAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('tempAuthSession');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('tempAuthSession');
};

// Manual token refresh function for components to use
export const refreshToken = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token available');
    }
    
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/refresh`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (response.data.success && response.data.token) {
      localStorage.setItem('token', response.data.token);
      sessionStorage.setItem('token', response.data.token);
      
      // Dispatch custom event to notify WebSocket manager of token refresh
      window.dispatchEvent(new CustomEvent('tokenRefreshed', {
        detail: { token: response.data.token }
      }));
      
      return response.data.token;
    }
    
    throw new Error('Token refresh failed');
  } catch (error) {
    console.error('Manual token refresh failed:', error);
    throw error;
  }
};

// Utility to get token expiration info for debugging
export const getTokenExpirationInfo = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return { error: 'No token found' };
    }
    
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiryTime = payload.exp * 1000;
    const currentTime = Date.now();
    const timeRemaining = expiryTime - currentTime;
    
    return {
      expiresAt: new Date(expiryTime),
      timeRemaining: timeRemaining,
      daysRemaining: Math.floor(timeRemaining / (24 * 60 * 60 * 1000)),
      hoursRemaining: Math.floor(timeRemaining / (60 * 60 * 1000)),
      isExpired: timeRemaining <= 0,
      needsRefresh: isTokenNearExpiry(token)
    };
  } catch (error) {
    return { error: 'Failed to parse token' };
  }
};